import { CONTENTFUL_TAGS } from '@/core/constants/contentfulTags';
import { logger } from '@/core/logger';
import { Platform } from 'react-native';

type Locale = 'en' | 'ar';
type EntriesQueries = Record<string, any>;

interface FetchResult {
  data: Record<string, any> | null;
  etag?: string;
}

const SPACE_ID = process.env.EXPO_PUBLIC_CONTENTFUL_SPACE_ID;
const ENVIRONMENT_ID = process.env.EXPO_PUBLIC_CONTENTFUL_ENVIRONMENT_ID || 'master';
const DELIVERY_TOKEN = process.env.EXPO_PUBLIC_CONTENTFUL_DELIVERY_TOKEN;
const HOST = 'cdn.contentful.com';

const BASE_URL =
  SPACE_ID && ENVIRONMENT_ID
    ? `https://${HOST}/spaces/${SPACE_ID}/environments/${ENVIRONMENT_ID}`
    : null;

// Log Contentful configuration on module load
if (SPACE_ID && DELIVERY_TOKEN) {
  logger.info('Contentful', 'Configuration loaded', {
    spaceId: SPACE_ID.substring(0, 8) + '...', // Partial for security
    environment: ENVIRONMENT_ID,
    hasToken: !!DELIVERY_TOKEN,
    host: HOST,
  });
} else {
  logger.warn('Contentful', 'Missing configuration', {
    hasSpaceId: !!SPACE_ID,
    hasToken: !!DELIVERY_TOKEN,
    environment: ENVIRONMENT_ID,
  });
}

export function mapAppLocaleToContentful(locale: Locale): string {
  return locale === 'en' ? 'en-US' : 'ar';
}

/** Normalize Contentful images field (array, collection, or single ref) to a flat list. */
function normalizeContentfulImageList(rawImages: any): any[] {
  if (Array.isArray(rawImages)) {
    return rawImages;
  }
  if (rawImages?.items && Array.isArray(rawImages.items)) {
    return rawImages.items;
  }
  if (rawImages) {
    return [rawImages];
  }
  return [];
}

/**
* Low-level REST helper for Contentful CDA
*/
async function fetchFromContentful(
  path: string,
  query: Record<string, string | number | boolean | undefined> = {},
): Promise<any> {
  if (!BASE_URL) {
    throw new Error('Contentful BASE_URL is not configured');
  }
  if (!DELIVERY_TOKEN) {
    throw new Error('Contentful DELIVERY token is not set');
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      params.append(key, value.join(','));
    } else {
      params.append(key, String(value));
    }
  });

  const url = `${BASE_URL}${path}${params.toString() ? `?${params.toString()}` : ''}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${DELIVERY_TOKEN}`,
  };
  // Native: every call uses the same URL, so the OS HTTP cache (iOS URL cache /
  // Android OkHttp) can answer from an earlier response for a while and a CMS
  // change would only show after that expires. Always ask the CDN instead; the
  // app keeps its own caches. Web keeps the browser's normal caching (and avoids
  // an extra CORS preflight).
  if (Platform.OS !== 'web') {
    headers['Cache-Control'] = 'no-cache';
    headers.Pragma = 'no-cache';
  }

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Contentful REST error ${res.status}: ${text}`);
  }

  return res.json();
}

/**
* SDK-compatible client implemented on top of REST.
* Only exposes the methods we actually use: getEntries and getEntry.
*/
export function getSdkClient() {
  if (!SPACE_ID) throw new Error('Contentful SPACE_ID is not set');
  if (!DELIVERY_TOKEN) throw new Error('Contentful DELIVERY token is not set');

  return {
    /**
     * Minimal getEntries implementation used by faqService, venturesService, legalDocumentsService.
     * Accepts the same query object we previously passed to the official SDK.
     */
    async getEntries(query: EntriesQueries = {}) {
      try {
        let data = await fetchFromContentful('/entries', query);

        // Special-case link resolution for ventures so images and featured items behave
        if (query.content_type === 'ventures' && data?.includes) {
          const entryById: Record<string, any> = {};
          const assetById: Record<string, any> = {};

          (data.includes.Entry || []).forEach((e: any) => {
            if (e?.sys?.id) entryById[e.sys.id] = e;
          });
          (data.includes.Asset || []).forEach((a: any) => {
            if (a?.sys?.id) assetById[a.sys.id] = a;
          });

          const resolveAssetLink = (link: any) => {
            const id = link?.sys?.id;
            const type = link?.sys?.type;
            const linkType = link?.sys?.linkType;
            if ((type === 'Asset' || (type === 'Link' && linkType === 'Asset')) && id && assetById[id]) {
              return assetById[id];
            }
            return link;
          };

          const resolveEntryLink = (link: any) => {
            const id = link?.sys?.id;
            const type = link?.sys?.type;
            const linkType = link?.sys?.linkType;
            if ((type === 'Entry' || (type === 'Link' && linkType === 'Entry')) && id && entryById[id]) {
              return entryById[id];
            }
            return link;
          };

          data.items = (data.items || []).map((item: any) => {
            if (!item?.fields) return item;
            const fields = { ...item.fields };

            // Resolve logo and hero image assets
            if (fields.ventureLogo) {
              fields.ventureLogo = resolveAssetLink(fields.ventureLogo);
            }
            if (fields.heroImage) {
              fields.heroImage = resolveAssetLink(fields.heroImage);
            }

            return { ...item, fields };
          });
        }

        logger.info('Contentful', 'REST getEntries success', {
          contentType: query.content_type,
          total: data?.total,
          itemsCount: data?.items?.length ?? 0,
        });
        // REST response already has { items, includes, total, limit, skip }
        return data;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.error('Contentful', 'REST getEntries failed', {
          contentType: query.content_type,
          error: errorMsg,
        });
        throw error;
      }
    },

    /**
     * Minimal getEntry implementation used by venturesService.
     */
    async getEntry(id: string, query: EntriesQueries = {}) {
      try {
        // Mimic SDK behaviour: fetch entry + includes via /entries with sys.id filter
        const data = await fetchFromContentful('/entries', {
          ...query,
          'sys.id': id,
        });

        const items = data?.items || [];
        let entry: any = items[0];

        // When fetching a venture detail (include: 2), resolve logo, hero and featured item images
        const entryType = entry?.sys?.contentType?.sys?.id;

        if (entryType === 'ventures' && data?.includes) {
          const includes = data.includes || {};
          const entryById: Record<string, any> = {};
          const assetById: Record<string, any> = {};

          (includes.Entry || []).forEach((e: any) => {
            if (e?.sys?.id) entryById[e.sys.id] = e;
          });
          (includes.Asset || []).forEach((a: any) => {
            if (a?.sys?.id) assetById[a.sys.id] = a;
          });

          const resolveAssetLink = (link: any) => {
            const id = link?.sys?.id;
            const type = link?.sys?.type;
            const linkType = link?.sys?.linkType;
            if ((type === 'Asset' || (type === 'Link' && linkType === 'Asset')) && id && assetById[id]) {
              return assetById[id];
            }
            return link;
          };

          const resolveEntryLink = (link: any) => {
            const id = link?.sys?.id;
            const type = link?.sys?.type;
            const linkType = link?.sys?.linkType;
            if ((type === 'Entry' || (type === 'Link' && linkType === 'Entry')) && id && entryById[id]) {
              return entryById[id];
            }
            return link;
          };

          const fields = { ...(entry.fields || {}) };

          // Resolve top-level logo and hero image
          if (fields.ventureLogo) {
            fields.ventureLogo = resolveAssetLink(fields.ventureLogo);
          }
          if (fields.heroImage) {
            fields.heroImage = resolveAssetLink(fields.heroImage);
          }

          // Resolve featured items entries and their image assets
          if (Array.isArray(fields.featuredItems)) {
            fields.featuredItems = fields.featuredItems
              .map((ref: any) => {
                const resolvedEntry = resolveEntryLink(ref);
                if (!resolvedEntry?.fields) return resolvedEntry;
                const fiFields = { ...resolvedEntry.fields };
                if (fiFields.image) {
                  fiFields.image = resolveAssetLink(fiFields.image);
                }
                return { ...resolvedEntry, fields: fiFields };
              })
              .filter(Boolean);
          }

          entry = { ...entry, fields };
        }

        logger.info('Contentful', 'REST getEntry success', {
          id,
        });
        return entry;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.error('Contentful', 'REST getEntry failed', {
          id,
          error: errorMsg,
        });
        throw error;
      }
    },
  };
}

async function fetchNamespacesByAudience(
  locale: Locale,
  audience: 'standard' | 'adha',
  _etag?: string,
): Promise<FetchResult> {
  const cfLocale = mapAppLocaleToContentful(locale);

  const query: EntriesQueries = {
    content_type: 'locales',
    include: 2, // linked entries up to 2 levels
    order: '-sys.updatedAt',
    limit: 1000,
    locale: cfLocale,
    ...(audience === 'adha'
      ? { 'metadata.tags.sys.id[in]': CONTENTFUL_TAGS.ADHA }
      : {}),
  };

  try {
    const data = await fetchFromContentful('/entries', query);
    const itemsCount = data?.items?.length || 0;
    const includesCount = data?.includes?.Entry?.length || 0;

    logger.info('Contentful', 'REST getEntries (locales) success', {
      locale: cfLocale,
      audience,
      total: data?.total,
      itemsCount,
      includesCount,
    });

    return { data };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    const isUnknownLocale = errorMsg.includes('Unknown locale');
    const errorDetails =
      error instanceof Error ? { message: error.message, stack: error.stack } : { error };

    logger.error('Contentful', 'fetchNamespacesByAudience failed', {
      locale: cfLocale,
      appLocale: locale,
      audience,
      ...errorDetails,
    });

    if (isUnknownLocale) {
      throw new Error(
        `Contentful locale '${cfLocale}' not found. ` +
        `Expected defaults are en-US for English and ar for Arabic. ` +
        `Please align your space locale codes or update the client mapping. ` +
        `Original error: ${errorMsg}`,
      );
    }

    throw error;
  }
}

/**
* Fetch standard translation entries from the `locales` model.
* ADHA-tagged entries are explicitly excluded to prevent collisions.
*/
export async function fetchAllNamespaces(
  locale: Locale,
  etag?: string,
): Promise<FetchResult> {
  return fetchNamespacesByAudience(locale, 'standard', etag);
}

/**
* Fetch only ADHA translation entries from the `locales` model.
* Returned data remains raw so the dedicated ADHA service can transform it.
*/
export async function fetchAdhaNamespaces(
  locale: Locale,
  etag?: string,
): Promise<FetchResult> {
  return fetchNamespacesByAudience(locale, 'adha', etag);
}

/**
 * Fetch `locales` entries tagged `adhaReservationStatus`.
 * Used for the ADHA journey and for a converted lead whose unit has
 * `isADHAUnit`. Source of truth for matched status messages:
 * `adha.reservationStatus.<normalized API status>`.
 * An unmatched status is shown as returned by the API.
 */
export async function fetchAdhaReservationStatusNamespaces(
  locale: Locale,
): Promise<FetchResult> {
  const cfLocale = mapAppLocaleToContentful(locale);
  const query: EntriesQueries = {
    content_type: 'locales',
    include: 2,
    order: '-sys.updatedAt',
    limit: 1000,
    locale: cfLocale,
    'metadata.tags.sys.id[in]': CONTENTFUL_TAGS.ADHA_RESERVATION_STATUS,
  };

  try {
    const data = await fetchFromContentful('/entries', query);
    logger.info('Contentful', 'REST getEntries (adhaReservationStatus locales) success', {
      locale: cfLocale,
      total: data?.total,
      itemsCount: data?.items?.length || 0,
    });
    return { data };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'fetchAdhaReservationStatusNamespaces failed', {
      locale: cfLocale,
      error: errorMsg,
    });
    throw error;
  }
}

/**
 * Keys on {@link PropertyAssets.unitImagesByUnitModel} that are not a `unitModel`.
 * `unit_model` matches a model key first, then `unitImages`, then `propertyImages`.
 */
export const UNIT_GALLERY_LOOKUP_KEYS = {
  unitImages: 'unitImages',
  propertyImages: 'propertyImages',
} as const;

/**
* Property image from Contentful
*/
export interface PropertyImage {
  url: string;
  title?: string;
  contentType?: string;
  width?: number;
  height?: number;
}

/**
* Optimize Contentful image URLs to prevent memory issues
* - Replaces downloads.ctfassets.net with images.ctfassets.net (optimized CDN)
* - Adds size constraints (max width 600px for mobile)
* - Adds quality parameter (80% for good quality/size balance)
* - Adds WebP format for better compression
*/
function optimizeContentfulImageUrl(url: string, maxWidth: number = 600): string {
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    // Replace downloads.ctfassets.net with images.ctfassets.net for optimized delivery
    let optimizedUrl = url.replace('downloads.ctfassets.net', 'images.ctfassets.net');

    const urlObj = new URL(optimizedUrl);
    urlObj.searchParams.set('w', maxWidth.toString());
    urlObj.searchParams.set('q', '80');
    urlObj.searchParams.set('fm', 'webp');

    return urlObj.toString();
  } catch (error) {
    logger.warn('Contentful', 'Failed to optimize image URL', { url, error });
    return url;
  }
}

/**
* Map a REST Asset to PropertyImage
* @param maxWidth - Optional max width for image URL (default 600). Use higher (e.g. 1200) for property detail carousel.
*/
function assetToPropertyImage(asset: any, maxWidth?: number): PropertyImage | null {
  if (!asset || !asset.fields?.file) return null;

  const fileField = asset.fields.file;
  // File can be localized: { 'en-US': { url, contentType, details } }
  const file =
    typeof fileField === 'object' && fileField['en-US']
      ? fileField['en-US']
      : fileField;

  const url = typeof file?.url === 'string' ? file.url : null;
  if (!url) return null;

  // Contentful URLs are relative, need to prepend https:
  let fullUrl: string;
  if (url.startsWith('//')) {
    fullUrl = `https:${url}`;
  } else if (url.startsWith('http')) {
    fullUrl = url;
  } else {
    fullUrl = `https:${url}`;
  }

  if (fullUrl && fullUrl.includes('ctfassets.net')) {
    fullUrl = optimizeContentfulImageUrl(fullUrl, maxWidth ?? 600);
  }

  return {
    url: fullUrl,
    title: typeof asset.fields?.title === 'string' ? asset.fields.title : undefined,
    contentType: typeof file?.contentType === 'string' ? file.contentType : undefined,
    width: file?.details?.image?.width ?? undefined,
    height: file?.details?.image?.height ?? undefined,
  };
}

/** Builds lookup map of asset ID to asset object from Contentful includes. */
function buildAssetMap(includesAssets?: any[]): Record<string, any> {
  const assetById: Record<string, any> = {};
  if (Array.isArray(includesAssets)) {
    includesAssets.forEach((asset) => {
      if (asset?.sys?.id) {
        assetById[asset.sys.id] = asset;
      }
    });
  }
  return assetById;
}

/** Resolves localized raw images and linked assets from Contentful includes. */
function resolveAssetsFromSource(imagesSource: any, assetById: Record<string, any>): any[] {
  if (!imagesSource) return [];

  let rawImages: any = imagesSource;
  if (rawImages && typeof rawImages === 'object' && !Array.isArray(rawImages)) {
    rawImages = rawImages['en-US'] || rawImages['en-us'] || rawImages;
  }

  const rawAssets: any[] = normalizeContentfulImageList(rawImages);

  return rawAssets
    .map((ref: any) => {
      const id = ref?.sys?.id;
      const type = ref?.sys?.type;
      const linkType = ref?.sys?.linkType;

      if ((type === 'Asset' || (type === 'Link' && linkType === 'Asset')) && id && assetById[id]) {
        return assetById[id];
      }
      return ref;
    })
    .filter(Boolean);
}

/** Resolves images source to a list of valid PropertyImage objects. */
function resolvePropertyImagesFromSource(
  imagesSource: any,
  assetById: Record<string, any>,
  maxWidth?: number,
): PropertyImage[] {
  const resolvedAssets = resolveAssetsFromSource(imagesSource, assetById);
  return resolvedAssets
    .map((asset) => assetToPropertyImage(asset, maxWidth))
    .filter((img): img is PropertyImage => !!img && !!img.url);
}

function normalizeContentfulAssetUrl(url: string): string {
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  if (url.startsWith('http')) {
    return url;
  }
  return `https:${url}`;
}

/** Plain string or locale-map field → trimmed string. */
function coerceContentfulString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['en-US', 'en', 'ar'] as const) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  }
  return undefined;
}

/** Number or locale-map / numeric string → finite number. */
function coerceContentfulNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['en-US', 'en', 'ar'] as const) {
      const nested = coerceContentfulNumber(record[key]);
      if (nested !== undefined) {
        return nested;
      }
    }
  }
  return undefined;
}

/**
 * Boolean Contentful field — only when the key is present as a real boolean.
 * Missing / wrong type → `undefined` (callers keep default UI behavior).
 */
function coerceContentfulBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  return undefined;
}

/** Description field: plain text, locale-map, or Rich Text (nodeType: 'document'). */
function coerceContentfulDescription(value: unknown): string | undefined {
  if (isContentfulRichText(value)) {
    const plain = extractRichTextPlain(value);
    return plain ? plain.trim() || undefined : undefined;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    for (const key of ['en-US', 'en', 'ar'] as const) {
      const resolved = coerceContentfulDescription(record[key]);
      if (resolved) {
        return resolved;
      }
    }
    return undefined;
  }
  return coerceContentfulString(value);
}

function brochureFileUrlFromAsset(brochureAsset: any): string | undefined {
  const brochureFileField = brochureAsset?.fields?.file;
  if (!brochureFileField) {
    return undefined;
  }

  let brochureFile = brochureFileField;
  if (typeof brochureFileField === 'object' && !Array.isArray(brochureFileField)) {
    if (typeof brochureFileField.url === 'string') {
      brochureFile = brochureFileField;
    } else {
      for (const key of ['en-US', 'en-us', 'en', 'ar'] as const) {
        const candidate = (brochureFileField as Record<string, unknown>)[key];
        if (candidate && typeof candidate === 'object' && typeof (candidate as { url?: unknown }).url === 'string') {
          brochureFile = candidate;
          break;
        }
      }
    }
  }

  const url = brochureFile?.url;
  if (typeof url !== 'string') {
    return undefined;
  }
  return normalizeContentfulAssetUrl(url);
}

/**
 * Unwrap locale maps (`{ 'en-US': … }`) when CDA returns unlocalized field shapes.
 * Link / Asset objects keep `sys` and must not be treated as locale maps.
 */
function unwrapLocalizedContentfulValue(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const record = value as Record<string, unknown>;
  if (record.sys) {
    return value;
  }
  for (const key of ['en-US', 'en-us', 'en', 'ar'] as const) {
    if (record[key] !== undefined) {
      return record[key];
    }
  }
  return value;
}

/**
 * Normalize brochure / voucher asset field to a flat list of refs.
 * Contentful may send: Link, Asset, Link[], `{ items: [] }`, or a locale map of any of those.
 */
function normalizeBrochureLinkList(brochureSource: unknown): unknown[] {
  const unwrapped = unwrapLocalizedContentfulValue(brochureSource);
  if (!unwrapped) {
    return [];
  }
  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }
  if (
    typeof unwrapped === 'object' &&
    Array.isArray((unwrapped as { items?: unknown }).items)
  ) {
    return (unwrapped as { items: unknown[] }).items;
  }
  return [unwrapped];
}

/**
 * Extracts brochure download URL(s) from brochure field reference.
 * Handles single Link/Asset object, arrays, locale maps, and `{ items }` collections.
 */
function extractBrochureUrls(
  brochureSource: unknown,
  assetById: Record<string, any>,
): string[] {
  const links = normalizeBrochureLinkList(brochureSource);
  const urls: string[] = [];

  for (const link of links) {
    if (!link || typeof link !== 'object') {
      continue;
    }

    // Rare: asset inlined on the field (object with fields.file) instead of a Link.
    const directUrl = brochureFileUrlFromAsset(link);
    if (directUrl) {
      urls.push(directUrl);
      continue;
    }

    const brochureId = (link as { sys?: { id?: string } }).sys?.id;
    if (!brochureId || !assetById[brochureId]) {
      continue;
    }
    const fileUrl = brochureFileUrlFromAsset(assetById[brochureId]);
    if (fileUrl) {
      urls.push(fileUrl);
    }
  }

  return urls;
}

/** Builds lookup map of entry ID to entry object from Contentful includes. */
function buildEntryMap(includesEntries?: any[]): Record<string, any> {
  const entryById: Record<string, any> = {};
  if (Array.isArray(includesEntries)) {
    includesEntries.forEach((entry) => {
      if (entry?.sys?.id) {
        entryById[entry.sys.id] = entry;
      }
    });
  }
  return entryById;
}

/**
 * Resolves a linked banner entry (`exploreVrBanner` / `masterPlan`).
 * Both content types share the same fields; only the background asset field id differs.
 * Returns undefined when the link is missing, the entry did not resolve, or
 * the CTA URL / copy is empty.
 */
function extractLinkedBanner(
  bannerLink: unknown,
  backgroundFieldId: string,
  entryById: Record<string, any>,
  assetById: Record<string, any>,
  maxWidth?: number,
): ExploreVrBannerData | undefined {
  if (!bannerLink || typeof bannerLink !== 'object') {
    return undefined;
  }

  const linkId = (bannerLink as { sys?: { id?: string } }).sys?.id;
  const bannerEntry =
    (linkId ? entryById[linkId] : null) ??
    ((bannerLink as { fields?: unknown }).fields ? bannerLink : null);
  const bannerFields = (bannerEntry as { fields?: Record<string, unknown> } | null)
    ?.fields;
  if (!bannerFields) {
    return undefined;
  }

  const label = coerceContentfulString(bannerFields.label)?.trim() ?? '';
  const title = coerceContentfulString(bannerFields.title)?.trim() ?? '';
  const buttonName =
    coerceContentfulString(bannerFields.buttonName)?.trim() ?? '';
  const startTourLink =
    coerceContentfulString(bannerFields.startTourLink)?.trim() ?? '';

  if (!startTourLink || (!label && !title)) {
    return undefined;
  }

  const backgroundImage = assetToPropertyImage(
    resolveSingleAssetRef(bannerFields[backgroundFieldId], assetById),
    maxWidth ?? 1200,
  );
  const buttonIcon = assetToPropertyImage(
    resolveSingleAssetRef(bannerFields.buttonIcon, assetById),
    64,
  );
  const backgroundImageUrl = imageAssetUrl(backgroundImage);
  const buttonIconUrl = imageAssetUrl(buttonIcon);

  return {
    label,
    title,
    buttonName,
    startTourLink,
    ...(backgroundImageUrl ? { backgroundImageUrl } : {}),
    ...(buttonIconUrl ? { buttonIconUrl } : {}),
  };
}

const UAE_PASS_LOGIN_BUTTON_TITLE = 'uaePassLoginButton';

export interface UaePassLoginButtonImage {
  url: string;
  width: number;
  height: number;
}

/**
 * Localized UAE Pass login artwork from Contentful `imageAsset`
 * (`title` = uaePassLoginButton). The linked asset file is localized,
 * so English and Arabic resolve to different images.
 * Arabic falls back to the English file when that locale has no image.
 */
export async function fetchUaePassLoginButtonImage(
  locale: Locale,
): Promise<UaePassLoginButtonImage | null> {
  try {
    const localized = await fetchImageAssetByTitle(
      UAE_PASS_LOGIN_BUTTON_TITLE,
      locale,
    );
    if (localized || locale === 'en') {
      return localized;
    }
    return await fetchImageAssetByTitle(UAE_PASS_LOGIN_BUTTON_TITLE, 'en');
  } catch (error) {
    logger.warn('Contentful', 'UAE Pass login button image unavailable', {
      locale,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

async function fetchImageAssetByTitle(
  title: string,
  locale: Locale,
): Promise<UaePassLoginButtonImage | null> {
  const data = await fetchFromContentful('/entries', {
    content_type: 'imageAsset',
    'fields.title': title,
    locale: mapAppLocaleToContentful(locale),
    include: 1,
    limit: 1,
  });
  const links = normalizeContentfulImageList(data?.items?.[0]?.fields?.image);
  const assetId = links[0]?.sys?.id;
  const included = (data?.includes?.Asset || []).find(
    (asset: { sys?: { id?: string } }) => asset?.sys?.id === assetId,
  );
  const asset = included ?? (links[0]?.fields ? links[0] : null);
  const url = imageAssetUrl(assetToPropertyImage(asset, 640));
  if (!url) {
    return null;
  }
  const file = resolveContentfulAssetFile(asset);
  const width = Number(file?.details?.image?.width);
  const height = Number(file?.details?.image?.height);
  return {
    url,
    width: Number.isFinite(width) && width > 0 ? width : 506,
    height: Number.isFinite(height) && height > 0 ? height : 100,
  };
}

function resolveContentfulAssetFile(asset: {
  fields?: { file?: unknown };
} | null): { details?: { image?: { width?: number; height?: number } } } | null {
  const fileField = asset?.fields?.file;
  if (!fileField || typeof fileField !== 'object') {
    return null;
  }
  if ('url' in fileField && typeof (fileField as { url?: unknown }).url === 'string') {
    return fileField as { details?: { image?: { width?: number; height?: number } } };
  }
  const localized = fileField as Record<string, { url?: string }>;
  return (localized['en-US'] ??
    Object.values(localized).find((file) => typeof file?.url === 'string') ??
    null) as { details?: { image?: { width?: number; height?: number } } } | null;
}

/** Banner art must be an image. PDFs and other files fall back to the solid fill / play icon. */
function imageAssetUrl(
  image: { url?: string; contentType?: string } | null,
): string | undefined {
  if (!image?.url) {
    return undefined;
  }
  const contentType = image.contentType?.toLowerCase() ?? '';
  if (contentType && !contentType.startsWith('image/')) {
    return undefined;
  }
  return image.url;
}

/**
 * Contentful `propertyImages.fields.unitModelImages` → `{ [unitModel]: images }`.
 * Linked `unitModelImages` entries supply `fields.unitModel` and `fields.images`.
 * Keys are lowercased so `1BHK` and `1bhk` match. Entries with no `unitModel` are skipped.
 * Duplicate models keep every image.
 */
function extractUnitImagesByUnitModel(
  fields: Record<string, unknown>,
  entryById: Record<string, { fields?: Record<string, unknown> }>,
  assetById: Parameters<typeof resolvePropertyImagesFromSource>[1],
  maxWidth?: number,
): Record<string, PropertyImage[]> {
  const links = normalizeBrochureLinkList(fields.unitModelImages);
  const imagesByUnitModel: Record<string, PropertyImage[]> = {};

  for (const link of links) {
    if (!link || typeof link !== 'object') {
      continue;
    }

    const linkId = (link as { sys?: { id?: string } }).sys?.id;
    const modelEntry =
      (linkId ? entryById[linkId] : null) ??
      ((link as { fields?: unknown }).fields ? link : null);
    const modelFields = (modelEntry as { fields?: Record<string, unknown> } | null)
      ?.fields;
    if (!modelFields) {
      continue;
    }

    const unitModel = coerceContentfulString(modelFields.unitModel)?.toLowerCase();
    if (!unitModel) {
      continue;
    }

    const images = resolvePropertyImagesFromSource(
      modelFields.images,
      assetById,
      maxWidth,
    );
    if (images.length === 0) {
      continue;
    }

    const existing = imagesByUnitModel[unitModel];
    imagesByUnitModel[unitModel] = existing ? [...existing, ...images] : images;
  }

  return imagesByUnitModel;
}

function resolveSingleAssetRef(
  source: unknown,
  assetById: Record<string, any>,
): unknown {
  if (!source || typeof source !== 'object') {
    return null;
  }
  const id = (source as { sys?: { id?: string } }).sys?.id;
  if (id && assetById[id]) {
    return assetById[id];
  }
  if ((source as { fields?: unknown }).fields) {
    return source;
  }
  return null;
}

function isPropertyEntryWithoutAssets(fields: Readonly<{
  imagesSource: unknown;
  unitImagesSource: unknown;
  unitModelImagesSource: unknown;
  brochureSource: unknown;
  exploreVrSource: unknown;
  masterPlanSource: unknown;
  viewLink: unknown;
  name: string | undefined;
  description: string | undefined;
  order: number | undefined;
  isUnitsAvailable: boolean | undefined;
}>): boolean {
  return (
    !fields.imagesSource &&
    !fields.unitImagesSource &&
    !fields.unitModelImagesSource &&
    !fields.brochureSource &&
    !fields.exploreVrSource &&
    !fields.masterPlanSource &&
    !fields.viewLink &&
    !fields.name &&
    !fields.description &&
    fields.order === undefined &&
    fields.isUnitsAvailable === undefined
  );
}

function buildPropertyAssetsFromEntry(
  propertyEntry: any,
  data: any,
  options?: FetchPropertyAssetsOptions,
): PropertyAssets {
  const fields = propertyEntry?.fields || {};
  const imagesSource = fields.images;
  const unitImagesSource = fields.unitImages;
  const unitModelImagesSource = fields.unitModelImages;
  const brochureSource = fields.brochureLink;
  const exploreVrSource = fields.exploreVrBAnner ?? fields.exploreVrBanner;
  const masterPlanSource = fields.masterPlan;
  const name = coerceContentfulString(fields.name);
  const description = coerceContentfulDescription(fields.description);
  const order = coerceContentfulNumber(fields.order);
  const isUnitsAvailable = coerceContentfulBoolean(fields.isUnitsAvailable);

  if (isPropertyEntryWithoutAssets({
    imagesSource,
    unitImagesSource,
    unitModelImagesSource,
    brochureSource,
    exploreVrSource,
    masterPlanSource,
    viewLink: fields.viewLink,
    name,
    description,
    order,
    isUnitsAvailable,
  })) {
    return { images: [] };
  }

  const assetById = buildAssetMap(data?.includes?.Asset);
  const entryById = buildEntryMap(data?.includes?.Entry);
  const images = imagesSource
    ? resolvePropertyImagesFromSource(imagesSource, assetById, options?.maxWidth)
    : [];
  const unitImages = unitImagesSource
    ? resolvePropertyImagesFromSource(
      unitImagesSource,
      assetById,
      options?.maxWidth,
    )
    : [];
  const brochureUrls = extractBrochureUrls(brochureSource, assetById);
  const exploreVrBanner = extractLinkedBanner(
    exploreVrSource,
    'aiBanner',
    entryById,
    assetById,
    options?.maxWidth,
  );
  const masterPlanBanner = extractLinkedBanner(
    masterPlanSource,
    'masterPlan',
    entryById,
    assetById,
    options?.maxWidth,
  );
  const unitImagesByUnitModel = extractUnitImagesByUnitModel(
    fields,
    entryById,
    assetById,
    options?.maxWidth,
  );
  if (unitImages.length > 0) {
    unitImagesByUnitModel[UNIT_GALLERY_LOOKUP_KEYS.unitImages] = unitImages;
  }
  if (images.length > 0) {
    unitImagesByUnitModel[UNIT_GALLERY_LOOKUP_KEYS.propertyImages] = images;
  }

  const result: PropertyAssets = { images };
  if (unitImages.length > 0) {
    result.unitImages = unitImages;
  }
  if (Object.keys(unitImagesByUnitModel).length > 0) {
    result.unitImagesByUnitModel = unitImagesByUnitModel;
    logger.info('Contentful', 'unitImagesByUnitModel', unitImagesByUnitModel);
  }
  if (name) {
    result.name = name;
  }
  if (description) {
    result.description = description;
  }
  if (order !== undefined) {
    result.order = order;
  }
  if (brochureUrls.length > 0) {
    result.brochureUrl = brochureUrls[0]!;
    result.brochureUrls = brochureUrls;
  }
  if (exploreVrBanner) {
    result.exploreVrBanner = exploreVrBanner;
  }
  if (masterPlanBanner) {
    result.masterPlanBanner = masterPlanBanner;
  }

  /** Older master-plan URL. Mobile uses it when the linked banner entry is absent. */
  const viewLink = coerceContentfulString(fields.viewLink)?.trim();
  if (viewLink) {
    result.viewLink = viewLink;
  }
  if (isUnitsAvailable !== undefined) {
    result.isUnitsAvailable = isUnitsAvailable;
  }

  return result;
}

/** Options for property image fetch (e.g. higher resolution for detail page). */
export interface FetchPropertyAssetsOptions {
  /** Max width for image URL. Default 600 (cards). Use 1200 for property detail carousel. */
  maxWidth?: number;
  /** App locale — maps to Contentful `en-US` / `ar` for localized name/description. */
  locale?: Locale;
  /**
   * Page size for tagged/list Contentful fetches (e.g. guest catalogue).
   * Defaults to {@link PROPERTY_ASSETS_TAG_PAGE_LIMIT}.
   */
  pageLimit?: number;
  /**
   * Skip the in-memory cache and fetch from Contentful (e.g. pull-to-refresh).
   * The fresh result still replaces the cached entry.
   */
  forceRefresh?: boolean;
}

/**
* Fetch property images by project name (fields.name) from Contentful.
* Used by PropertyDetailsScreen (mobile), PropertyDetailsScreenWeb, and usePropertyImages hook.
*
* @param projectName - The project name string (e.g. 'Al Metlaa') used to filter entries by fields.name
* @param options     - Optional fetch options (e.g. maxWidth for image URL)
*/
export async function fetchPropertyImages(
  projectName: string,
  options?: FetchPropertyAssetsOptions,
): Promise<PropertyImage[]> {
  const assets = await fetchPropertyAssetsByName(projectName, options);
  return assets.images;
}

/**
 * Fetch images + brochure (+ localized name/description) by Contentful `fields.name`.
 * My-properties web uses this when `project_id` is not on the owned unit.
 */
export async function fetchPropertyAssetsByName(
  projectName: string,
  options?: FetchPropertyAssetsOptions,
): Promise<PropertyAssets> {
  if (!SPACE_ID || !DELIVERY_TOKEN) {
    logger.warn('Contentful', 'Missing configuration, skipping fetchPropertyAssetsByName', {
      projectName,
    });
    return { images: [] };
  }

  try {
    const locale = options?.locale ?? 'en';
    const baseQuery: EntriesQueries = {
      content_type: 'propertyImages',
      include: 2,
      limit: 1,
      locale: mapAppLocaleToContentful(locale),
    };

    // Try exact match first, then fall back to a fuzzy match
    let data = await fetchFromContentful('/entries', {
      ...baseQuery,
      'fields.name': projectName,
    });
    let items = data?.items || [];

    if (items.length === 0) {
      data = await fetchFromContentful('/entries', {
        ...baseQuery,
        'fields.name[match]': projectName,
      });
      items = data?.items || [];
    }

    if (items.length === 0) {
      logger.info('Contentful', 'fetchPropertyAssetsByName: no entry found', { projectName });
      return { images: [] };
    }

    const propertyEntry = items[0];
    if (!propertyEntry) {
      return { images: [] };
    }

    const result = buildPropertyAssetsFromEntry(propertyEntry, data, options);
    logger.info('Contentful', 'fetchPropertyAssetsByName success', {
      projectName,
      locale,
      imageCount: result.images.length,
      brochureCount: result.brochureUrls?.length ?? 0,
      hasName: Boolean(result.name),
      hasDescription: Boolean(result.description),
    });
    return result;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'fetchPropertyAssetsByName failed', {
      projectName,
      error: errorMsg,
    });
    return { images: [] };
  }
}

export interface ExploreVrBannerData {
  readonly label: string;
  readonly title: string;
  readonly buttonName: string;
  readonly startTourLink: string;
  readonly backgroundImageUrl?: string;
  readonly buttonIconUrl?: string;
}

export interface PropertyAssets {
  images: PropertyImage[];
  /**
   * Contentful `propertyImages.fields.unitImages` — unit-card / unit-detail
   * gallery assets for this project (not a separate content type).
   */
  unitImages?: PropertyImage[];
  /**
   * Gallery lookup for a unit.
   * Model keys come from `unitModelImages` (`1BHK`, …).
   * `unitImages` and `propertyImages` are the two fallbacks when `unit_model` does not match.
   */
  unitImagesByUnitModel?: Record<string, PropertyImage[]>;
  /** Localized Contentful `fields.name` when present. */
  name?: string;
  /** Localized Contentful `fields.description` when present. */
  description?: string;
  /** Contentful `fields.order` — used to pin guest listing order. */
  order?: number;
  /** First brochure URL (backward compatible). */
  brochureUrl?: string;
  /** All brochure asset URLs from Contentful (single Link or array). */
  brochureUrls?: string[];
  /**
   * Contentful `propertyImages.fields.viewLink`.
   * Mobile uses this as the master-plan link when the linked `masterPlan` entry
   * is absent. Web home uses `masterPlanBanner` only.
   */
  viewLink?: string;
  /**
   * Linked `exploreVrBanner` entry from `propertyImages.fields.exploreVrBanner`
   * (also accepts legacy `exploreVrBAnner`). Absent when CMS has no banner.
   */
  exploreVrBanner?: ExploreVrBannerData;
  /**
   * Linked `masterPlan` entry from `propertyImages.fields.masterPlan`.
   * Same shape as the VR banner (background asset field is `masterPlan`).
   * Absent when CMS has no master-plan banner. Mobile falls back to `viewLink`.
   */
  masterPlanBanner?: ExploreVrBannerData;
  /**
   * Contentful `propertyImages.fields.isUnitsAvailable`.
   * When explicitly `false`, Reserve Unit must be hidden for every unit of this project.
   * `undefined` when the field is absent from the entry.
   */
  isUnitsAvailable?: boolean;
}

function isContentfulRichText(
  value: unknown,
): value is { readonly nodeType: string; readonly content?: readonly unknown[] } {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { nodeType?: unknown }).nodeType === 'document',
  );
}

function extractRichTextPlain(node: unknown): string {
  if (typeof node === 'string') {
    return node;
  }
  if (!node || typeof node !== 'object') {
    return '';
  }
  const record = node as { value?: unknown; content?: readonly unknown[] };
  const parts: string[] = [];
  if (typeof record.value === 'string') {
    parts.push(record.value);
  }
  if (Array.isArray(record.content)) {
    for (const child of record.content) {
      const text = extractRichTextPlain(child);
      if (text) {
        parts.push(text);
      }
    }
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/**
* Fetch property images and brochure by project ID using REST API.
* Mirrors the previous SDK-based behaviour but without the SDK.
*/
const propertyAssetsCache = new Map<string, PropertyAssets>();

function propertyAssetsCacheKey(
  projectId: string,
  maxWidth: number = 600,
  locale: Locale = 'en',
): string {
  // v6: master-plan banner is mapped from the linked `masterPlan` entry
  return `v6:${projectId}:${maxWidth}:${locale}`;
}

/** Drop every cached project/unit asset set so the next fetch reads Contentful again. */
export function clearPropertyAssetsCache(): void {
  propertyAssetsCache.clear();
}

export function peekPropertyAssets(
  projectId: string,
  maxWidth?: number,
  locale?: Locale,
): PropertyAssets | null {
  return (
    propertyAssetsCache.get(propertyAssetsCacheKey(projectId, maxWidth, locale)) ??
    null
  );
}

export async function fetchPropertyAssets(
  projectId: string,
  options?: FetchPropertyAssetsOptions,
): Promise<PropertyAssets> {
  const cacheKey = propertyAssetsCacheKey(
    projectId,
    options?.maxWidth,
    options?.locale,
  );
  if (!options?.forceRefresh) {
    const cached = propertyAssetsCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const assets = await fetchPropertyAssetsFromNetwork(projectId, options);
  if (
    assets.images.length > 0 ||
    (assets.unitImages?.length ?? 0) > 0 ||
    Object.keys(assets.unitImagesByUnitModel ?? {}).length > 0 ||
    assets.brochureUrl ||
    (assets.brochureUrls?.length ?? 0) > 0 ||
    assets.viewLink ||
    assets.name ||
    assets.description ||
    assets.order !== undefined ||
    assets.exploreVrBanner ||
    assets.masterPlanBanner
  ) {
    propertyAssetsCache.set(cacheKey, assets);
  }
  return assets;
}

async function fetchPropertyAssetsFromNetwork(
  projectId: string,
  options?: FetchPropertyAssetsOptions,
): Promise<PropertyAssets> {
  if (!SPACE_ID || !DELIVERY_TOKEN) {
    logger.warn('Contentful', 'Missing configuration, skipping property assets fetch', {
      projectId,
    });
    return { images: [] };
  }

  try {
    const locale = options?.locale ?? 'en';
    const query: EntriesQueries = {
      content_type: 'propertyImages',
      'fields.projectId': projectId,
      include: 2,
      limit: 1,
      locale: mapAppLocaleToContentful(locale),
    };

    const data = await fetchFromContentful('/entries', query);
    const items = data?.items || [];

    if (items.length === 0) {
      logger.info('Contentful', 'No property assets found', { projectId, locale });
      return { images: [] };
    }

    const propertyEntry = items[0];
    if (!propertyEntry) {
      logger.info('Contentful', 'Property entry is undefined', { projectId });
      return { images: [] };
    }

    const result = buildPropertyAssetsFromEntry(propertyEntry, data, options);
    logger.info('Contentful', 'Property assets fetched', {
      projectId,
      locale,
      imageCount: result.images.length,
      brochureCount: result.brochureUrls?.length ?? 0,
      hasName: Boolean(result.name),
      hasDescription: Boolean(result.description),
    });
    return result;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'Failed to fetch property assets', {
      projectId,
      error: errorMsg,
    });
    return { images: [] };
  }
}

const PROPERTY_ASSETS_TAG_PAGE_LIMIT = 100;

export interface PropertyAssetsWithProjectId extends PropertyAssets {
  readonly projectId: string;
}

export interface TaggedPropertyAssetsResult {
  readonly tagId: string;
  readonly locale: string;
  /** All tagged property entries as an array (Contentful order). */
  readonly assets: ReadonlyArray<PropertyAssetsWithProjectId>;
  /** Same assets keyed by Contentful `fields.projectId` (Boomi project id). */
  readonly assetsByProjectId: Readonly<Record<string, PropertyAssets>>;
  /** Raw CDA response (single page object, or `{ pages, total }` when paginated). */
  readonly rawResponse: unknown;
}

/**
 * One tagged page, then the next, in order. Recursion keeps each request
 * sequential (the next skip depends on this page's total) without awaiting in a loop.
 */
async function collectTaggedPropertyAssetPages(params: Readonly<{
  tagId: string;
  cfLocale: string;
  pageLimit: number;
  skip: number;
  options: FetchPropertyAssetsOptions | undefined;
  assets: PropertyAssetsWithProjectId[];
  assetsByProjectId: Record<string, PropertyAssets>;
  pages: unknown[];
}>): Promise<number> {
  const query: EntriesQueries = {
    content_type: 'propertyImages',
    'metadata.tags.sys.id[in]': params.tagId,
    include: 2,
    limit: params.pageLimit,
    skip: params.skip,
    locale: params.cfLocale,
    order: 'sys.createdAt',
  };

  const data = await fetchFromContentful('/entries', query);
  params.pages.push(data);
  const totalValue = data?.total;
  const total = typeof totalValue === 'number' ? totalValue : 0;
  const rawItems = data?.items;
  const items = Array.isArray(rawItems) ? rawItems : [];
  appendTaggedPropertyAssetsFromPage(
    items,
    data,
    params.options,
    params.assets,
    params.assetsByProjectId,
  );

  const nextSkip = params.skip + params.pageLimit;
  logger.info('Contentful', 'fetchPropertyAssetsByTag page', {
    tagId: params.tagId,
    locale: params.cfLocale,
    pageLimit: params.pageLimit,
    fetched: items.length,
    total,
    mapped: params.assets.length,
    nextSkip,
  });

  if (nextSkip < total) {
    return collectTaggedPropertyAssetPages({ ...params, skip: nextSkip });
  }
  return total;
}

function appendTaggedPropertyAssetsFromPage(
  items: ReadonlyArray<any>,
  data: any,
  options: FetchPropertyAssetsOptions | undefined,
  assets: PropertyAssetsWithProjectId[],
  assetsByProjectId: Record<string, PropertyAssets>,
): void {
  for (const entry of items) {
    const projectId = coerceContentfulString(entry?.fields?.projectId);
    if (!projectId) {
      continue;
    }
    const mapped = buildPropertyAssetsFromEntry(entry, data, options);
    assets.push({ ...mapped, projectId });
    assetsByProjectId[projectId] = mapped;
    propertyAssetsCache.set(
      propertyAssetsCacheKey(projectId, options?.maxWidth, options?.locale),
      mapped,
    );
  }
}

/**
 * Fetch all `propertyImages` entries that carry a Contentful metadata tag
 * (e.g. {@link CONTENTFUL_TAGS.GUEST}). One tagged request returns the full
 * property array — not one call per `projectId`.
 */
export async function fetchPropertyAssetsByTag(
  tagId: string,
  options?: FetchPropertyAssetsOptions,
): Promise<TaggedPropertyAssetsResult> {
  const locale = options?.locale ?? 'en';
  const cfLocale = mapAppLocaleToContentful(locale);
  const empty: TaggedPropertyAssetsResult = {
    tagId,
    locale: cfLocale,
    assets: [],
    assetsByProjectId: {},
    rawResponse: null,
  };

  if (!SPACE_ID || !DELIVERY_TOKEN) {
    logger.warn('Contentful', 'Missing configuration, skipping fetchPropertyAssetsByTag', {
      tagId,
    });
    return empty;
  }

  if (!tagId.trim()) {
    logger.warn('Contentful', 'fetchPropertyAssetsByTag called with empty tagId');
    return empty;
  }

  try {
    const pageLimit =
      typeof options?.pageLimit === 'number' &&
        Number.isFinite(options.pageLimit) &&
        options.pageLimit > 0
        ? Math.floor(options.pageLimit)
        : PROPERTY_ASSETS_TAG_PAGE_LIMIT;
    const assets: PropertyAssetsWithProjectId[] = [];
    const assetsByProjectId: Record<string, PropertyAssets> = {};
    const pages: unknown[] = [];

    const total = await collectTaggedPropertyAssetPages({
      tagId,
      cfLocale,
      pageLimit,
      skip: 0,
      options,
      assets,
      assetsByProjectId,
      pages,
    });

    const firstPage = pages[0] ?? null;
    const rawResponse =
      pages.length <= 1 ? firstPage : { pages, total };

    logger.info('Contentful', 'fetchPropertyAssetsByTag success', {
      tagId,
      locale: cfLocale,
      projectCount: assets.length,
      total,
    });

    return {
      tagId,
      locale: cfLocale,
      assets,
      assetsByProjectId,
      rawResponse,
    };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'fetchPropertyAssetsByTag failed', {
      tagId,
      locale: cfLocale,
      error: errorMsg,
    });
    return empty;
  }
}

/**
* Fetch home cover image by key from Contentful.
* Uses the same propertyImages content type as property images.
*/
export async function fetchHomeCoverImage(key: string = 'home-cover'): Promise<string | null> {
  if (!SPACE_ID || !DELIVERY_TOKEN) {
    logger.warn('Contentful', 'Missing configuration, skipping home cover image fetch', {
      key,
    });
    return null;
  }

  try {
    const baseQuery: EntriesQueries = {
      content_type: 'propertyImages',
      include: 2,
      limit: 1,
    };

    // Try exact match first, then fallback to a fuzzy match to handle naming variants.
    let data = await fetchFromContentful('/entries', {
      ...baseQuery,
      'fields.name': key,
    });
    let items = data?.items || [];

    if (items.length === 0) {
      data = await fetchFromContentful('/entries', {
        ...baseQuery,
        'fields.name[match]': key,
      });
      items = data?.items || [];
    }

    if (items.length === 0) {
      logger.info('Contentful', 'No home cover image found', { key });
      return null;
    }

    const entry = items[0];
    if (!entry) {
      logger.info('Contentful', 'Home cover entry is undefined', { key });
      return null;
    }

    const fields = entry.fields || {};
    const imagesSource =
      fields.images ??
      fields.image ??
      fields.coverImage ??
      fields.coverImages;
    if (!imagesSource) {
      logger.info('Contentful', 'Home cover entry has no image field', { key });
      return null;
    }

    const assetById = buildAssetMap(data?.includes?.Asset);
    const resolvedAssets = resolveAssetsFromSource(imagesSource, assetById);

    const firstAsset = resolvedAssets[0];
    if (!firstAsset) {
      logger.info('Contentful', 'Home cover entry has no valid image asset', { key });
      return null;
    }

    const image = assetToPropertyImage(firstAsset);
    if (!image?.url) {
      logger.info('Contentful', 'Home cover image has no URL', { key });
      return null;
    }

    // For the home cover banner we can safely request a higher resolution image,
    // so it looks crisp on larger (especially web) screens.
    let optimizedUrl = image.url;
    if (optimizedUrl && optimizedUrl.includes('ctfassets.net')) {
      // Use a larger maxWidth than the default (600) for hero/banner usage.
      optimizedUrl = optimizeContentfulImageUrl(optimizedUrl, 1200);
    }

    logger.info('Contentful', 'Home cover image fetched', {
      key,
      url: optimizedUrl,
    });

    return optimizedUrl;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'Failed to fetch home cover image', {
      key,
      error: errorMsg,
    });
    return null;
  }
}

// ---------------------------------------------------------------------------
// Login feature config
// ---------------------------------------------------------------------------

export interface LoginConfig {
  /** Show the UAE Pass login button in the login dialog. */
  isUAEpassLogin: boolean;
  /** Show the Emirates ID login button in the login dialog. */
  isEmiratesIdLogin: boolean;
}

/**
 * Fetch the `config` content-type entry tagged `config`.
 * Returns `null` when Contentful is unavailable or the entry is missing.
 *
 * The promise is cached at module level so multiple callers (e.g. Welcome
 * remounting on navigation) share a single in-flight / resolved request.
 */
let loginConfigPromise: Promise<LoginConfig | null> | null = null;

async function doFetchLoginConfig(): Promise<LoginConfig | null> {
  try {
    const client = getSdkClient();
    const data = await client.getEntries({
      content_type: CONTENTFUL_TAGS.CONFIG,
      'metadata.tags.sys.id[in]': CONTENTFUL_TAGS.CONFIG,
      limit: 1,
    });

    const entry = data?.items?.[0];
    if (!entry) {
      logger.warn('Contentful', 'fetchLoginConfig: no config entry found');
      return null;
    }

    const fields = entry.fields ?? {};
    // Field names as published in Contentful (note: CF uses 'isUaEpassLogin' and
    // 'isEmiratedIdLogin' — the latter is a known typo in the CMS; match exactly).
    const isUAEpassLogin = fields.isUaEpassLogin === true;
    const isEmiratesIdLogin = fields.isEmiratesIdLogin === true;

    logger.info('Contentful', 'fetchLoginConfig success', {
      isUAEpassLogin,
      isEmiratesIdLogin,
    });

    return { isUAEpassLogin, isEmiratesIdLogin };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'fetchLoginConfig failed', { error: errorMsg });
    // Clear the cache on error so the next call retries.
    loginConfigPromise = null;
    return null;
  }
}

export function fetchLoginConfig(): Promise<LoginConfig | null> {
  loginConfigPromise ??= doFetchLoginConfig();
  return loginConfigPromise;
}

const loginConfigListeners = new Set<(config: LoginConfig) => void>();

/** Notified with the new config after {@link refreshLoginConfig} succeeds. */
export function subscribeLoginConfig(listener: (config: LoginConfig) => void): () => void {
  loginConfigListeners.add(listener);
  return () => {
    loginConfigListeners.delete(listener);
  };
}

/**
 * Re-fetch the login config from Contentful (e.g. app back in foreground).
 * Subscribers only hear about a successful result, so a failed refresh keeps
 * the config already on screen.
 */
export async function refreshLoginConfig(): Promise<LoginConfig | null> {
  loginConfigPromise = doFetchLoginConfig();
  const config = await loginConfigPromise;
  if (config) {
    loginConfigListeners.forEach((listener) => {
      try {
        listener(config);
      } catch {
        // Ignore subscriber errors so one bad listener cannot block others
      }
    });
  }
  return config;
}



/**
* One `filters` entry, already resolved to plain values — no Contentful
* response shape (fields/sys/includes) leaks past this function.
*/
export interface FilterItem {
  label: string | undefined;
  value: string | undefined;
  order: number | undefined;
  filterType: string | undefined;
  /** Real HTTPS URL of the linked icon Asset, or null if unset/unresolved. */
  iconUrl: string | null;
}

/**
* Fetch `filters` entries, optionally scoped to one `filterType` (e.g.
* "Amenities"), and resolve each entry's linked `icon` Asset to a plain URL.
* All transformation happens here — callers get ready-to-use FilterItem[],
* never raw CDA `items`/`includes` shapes.
*/
export async function fetchFilters(
  locale: Locale,
  filterType?: string,
): Promise<FilterItem[]> {
  const cfLocale = mapAppLocaleToContentful(locale);
  const query: EntriesQueries = {
    content_type: 'filters',
    include: 1,
    order: 'fields.order',
    limit: 1000,
    locale: cfLocale,
    ...(filterType ? { 'fields.filterType': filterType } : {}),
  };

  try {
    const data = await fetchFromContentful('/entries', query);

    const iconUrlById = new Map<string, string | null>(
      (data?.includes?.Asset ?? []).map((asset: any) => [
        asset.sys.id,
        asset.fields?.file?.url
          ? normalizeContentfulAssetUrl(asset.fields.file.url)
          : null,
      ]),
    );

    const items: FilterItem[] = (data?.items ?? []).map((item: any) => {
      const iconId = item.fields?.icon?.sys?.id;
      return {
        label: item.fields?.label,
        value: item.fields?.value,
        order: item.fields?.order,
        filterType: item.fields?.filterType,
        iconUrl: iconId ? iconUrlById.get(iconId) ?? null : null,
      };
    });

    logger.info('Contentful', 'REST getEntries (filters) success', {
      locale: cfLocale,
      filterType,
      total: data?.total,
      itemsCount: items.length,
    });
    return items;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Contentful', 'fetchFilters failed', {
      locale: cfLocale,
      filterType,
      error: errorMsg,
    });
    throw error;
  }
}
